import React from 'react';
import { motion } from 'framer-motion';
import { DropletsIcon, SmartphoneIcon } from 'lucide-react';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { WhyPopover } from '../../ui/WhyPopover';
import { useDb } from '../../../hooks/useDb';
import { deviceById, roomById } from '../../../utils/dbSelectors';

/** Safety alarm in Slapen — calm but clear, steady colour, never flashing. */
export function AlarmCard({
  onAck
}: {
  onAck: () => void;
}) {
  const {
    sensor,
    room,
    time
  } = useDb(t => {
    const s = deviceById(t, 'water-keuken');
    return {
      sensor: s,
      room: s ? roomById(t, s.roomId) : undefined,
      time: t.meta.time
    };
  });
  return <motion.article role="alert" initial={{
    opacity: 0,
    y: 12,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="w-full max-w-[760px] rounded-[28px] border-2 border-alarm/70 bg-night-900 p-7 text-left" data-id="element-1260" data-mp="1cgxz0ban9zrj">
      <div className="flex items-start gap-5" data-id="element-1261" data-mp="3tc4h5yfw27dy">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-alarm/15 text-alarm" data-id="element-1262" data-mp="3tabc2nkxbv82">
          <DropletsIcon className="h-7 w-7" strokeWidth={1.75} aria-hidden data-id="element-1263" data-mp="1zjnlrwlzd1a" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-1264" data-mp="3jc5bc9ausv4s">
          <p className="text-[22px] font-medium text-alarm" data-id="element-1265" data-mp="3ocrxtno87ig4">Veiligheidsalarm · {time}</p>
          <h2 className="mt-1 text-[36px] font-semibold leading-tight text-ink" data-id="element-1266" data-mp="fxrgnoqixu76">
            Water in de {room?.name.toLowerCase() ?? 'keuken'}
          </h2>
          <p className="mt-2 text-[22px] leading-snug text-ink-soft" data-id="element-1267" data-mp="kvt7ivu6yxt7">
            {sensor?.name ?? 'De watersensor'} is nat. Kijk even of er iets lekt.
          </p>
          <p className="mt-3 flex items-center gap-2.5 text-[22px] text-ink-muted" data-id="element-1268" data-mp="axdiqzin00mv">
            <SmartphoneIcon className="h-6 w-6" aria-hidden data-id="element-1269" data-mp="2rmsq1vfwgnyl" /> Staat ook op je telefoon
          </p>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/[0.07] pt-5" data-id="element-1270" data-mp="2vpf8x6uleyjn">
        <ProcessingTag where="lokaal" size="wall" data-id="element-1271" data-mp="124lrz8k65m2k" />
        <WhyPopover traceId="waterlek" size="wall" data-id="element-1272" data-mp="x6txq7w1xnv7" />
        <button type="button" onClick={onAck} className="ml-auto h-14 whitespace-nowrap rounded-full bg-ink px-8 text-[22px] font-semibold text-night-950 transition-colors duration-150 hover:bg-white" data-id="element-1273" data-mp="x7c5a0o5zoif">
          Gezien
        </button>
      </div>
    </motion.article>;
}